import type { DefectBandSlug } from '../map/buildRulesFromColorScheme';
import { DEFECT_BAND_SLUGS } from '../map/buildRulesFromColorScheme';


export type DefectBandMeaningFamily =
  | 'rci'
  | 'pas'
  | 'pci'
  | 'cracking'
  | 'rutting'
  | 'ride'
  | 'texture'
  | 'scrim'
  | 'curvature'
  | 'edge'
  | 'condition';

export type DefectBandMeaningLabels = Readonly<Record<DefectBandSlug, string>>;

export interface DefectBandMeaningContext {
  readonly surveyType?: string | null;
  readonly measurementSlug?: string | null;
  readonly parameter?: string | null;
  readonly code?: string | null;
  readonly measurementName?: string | null;
  readonly defectType?: string | null;
}

const RCI_LABELS: DefectBandMeaningLabels = {
  'very-low': 'Green — Good road condition',
  low: 'Green–Amber — Generally sound',
  medium: 'Amber — Moderate deterioration',
  high: 'Amber–Red — Significant deterioration',
  'very-high': 'Red — Poor road condition',
};

const PAS_LABELS: DefectBandMeaningLabels = {
  'very-low': 'Green — Good pavement assessment',
  low: 'Green–Amber — Generally sound',
  medium: 'Amber — Moderate maintenance need',
  high: 'Amber–Red — Significant maintenance need',
  'very-high': 'Red — Poor pavement assessment',
};

const PCI_LABELS: DefectBandMeaningLabels = {
  'very-low': 'Green — Good / excellent PCI',
  low: 'Green–Amber — Satisfactory PCI',
  medium: 'Amber — Fair PCI',
  high: 'Amber–Red — Poor PCI',
  'very-high': 'Red — Very poor / failed PCI',
};

const CRACKING_LABELS: DefectBandMeaningLabels = {
  'very-low': 'Green — Negligible cracking',
  low: 'Green–Amber — Light cracking',
  medium: 'Amber — Moderate cracking intensity',
  high: 'Amber–Red — Heavy cracking',
  'very-high': 'Red — Severe cracking',
};

const RUTTING_LABELS: DefectBandMeaningLabels = {
  'very-low': 'Green — Minimal rutting',
  low: 'Green–Amber — Light rutting',
  medium: 'Amber — Moderate rut depth',
  high: 'Amber–Red — Significant rutting',
  'very-high': 'Red — Severe rutting',
};

const RIDE_LABELS: DefectBandMeaningLabels = {
  'very-low': 'Blue — Good ride quality',
  low: 'Blue–Amber — Acceptable ride',
  medium: 'Amber — Noticeable unevenness',
  high: 'Amber–Orange — Poor ride quality',
  'very-high': 'Orange–Red — Severe unevenness',
};

const TEXTURE_LABELS: DefectBandMeaningLabels = {
  'very-low': 'Red — Inadequate texture depth',
  low: 'Red–Amber — Below desirable texture',
  medium: 'Amber — Marginal texture depth',
  high: 'Amber–Green — Adequate texture',
  'very-high': 'Green — Good texture depth',
};

const SCRIM_LABELS: DefectBandMeaningLabels = {
  'very-low': 'Blue — Comfortably above investigatory level',
  low: 'Blue–Amber — Above investigatory level',
  medium: 'Amber — Approaching investigatory level',
  high: 'Amber–Red — At investigatory level',
  'very-high': 'Red — Below investigatory level',
};

const CURVATURE_LABELS: DefectBandMeaningLabels = {
  'very-low': 'Grey — Gentle curvature',
  low: 'Grey — Mild curvature',
  medium: 'Slate — Moderate curvature',
  high: 'Dark — Sharp curvature',
  'very-high': 'Near-black — Very sharp curvature',
};

const EDGE_LABELS: DefectBandMeaningLabels = {
  'very-low': 'Purple — Sound edge condition',
  low: 'Purple–Amber — Early edge wear',
  medium: 'Amber — Moderate edge deterioration',
  high: 'Amber–Red — Significant edge failure',
  'very-high': 'Red — Severe edge deterioration',
};

const CONDITION_LABELS: DefectBandMeaningLabels = {
  'very-low': 'Green — Good condition',
  low: 'Green–Amber — Generally sound',
  medium: 'Amber — Moderate deterioration',
  high: 'Amber–Red — Significant deterioration',
  'very-high': 'Red — Poor condition',
};

const FAMILY_LABELS: Readonly<Record<DefectBandMeaningFamily, DefectBandMeaningLabels>> = {
  rci: RCI_LABELS,
  pas: PAS_LABELS,
  pci: PCI_LABELS,
  cracking: CRACKING_LABELS,
  rutting: RUTTING_LABELS,
  ride: RIDE_LABELS,
  texture: TEXTURE_LABELS,
  scrim: SCRIM_LABELS,
  curvature: CURVATURE_LABELS,
  edge: EDGE_LABELS,
  condition: CONDITION_LABELS,
};

const FAMILY_DESCRIPTIONS: Readonly<Record<DefectBandMeaningFamily, string>> = {
  rci:
    'Revised SCANNER Road Condition Indicator bands. Green = good; Amber (>40) = moderate deterioration; Red (>100) = poor road condition.',
  pas:
    'Pavement Assessment Score bands used for authority-level maintenance prioritisation. Green = good; Red = poor assessment.',
  pci:
    'Pavement Condition Index bands (0–100 scale). Green = good/excellent; Red = very poor/failed condition.',
  cracking:
    'Cracking intensity bands from low (green) to severe (red), used for resurface / strengthen investigation.',
  rutting:
    'Rut depth / rut index bands from minimal (green) to severe (red) wheel-path deformation.',
  ride:
    'Longitudinal profile / ride-quality bands from good (blue) to severe unevenness (orange–red).',
  texture:
    'SMTD / texture-depth bands from inadequate (red) to good (green). Texture supports skid review; it is not a substitute for SCRIM.',
  scrim:
    'SCRIM investigatory-level bands from comfortably above IL (blue) to below IL (red). Used for low slip-speed skid risk review.',
  curvature:
    'Road geometry / curvature inventory bands from gentle (grey) to very sharp (dark).',
  edge:
    'Edge condition bands from sound (purple) to severe edge deterioration (red).',
  condition:
    'Condition bands from good (green) through moderate (amber) to poor (red).',
};

function normalizeToken(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

export function resolveDefectBandMeaningFamily(
  context: DefectBandMeaningContext,
): DefectBandMeaningFamily {
  const surveyType = normalizeToken(context.surveyType);
  const slug = normalizeToken(context.measurementSlug);
  const parameter = normalizeToken(context.parameter);
  const code = normalizeToken(context.code);
  const name = normalizeToken(context.measurementName);
  const defectType = normalizeToken(context.defectType);
  const haystack = `${slug} ${parameter} ${code} ${name} ${defectType}`;

  if (surveyType === 'scrim' || /\bscrim\b|\bskid\b|\bsr50\b/.test(haystack) || parameter === 'scrimil') {
    return 'scrim';
  }
  if (/\brci\b|road condition indicator/.test(haystack) && !/\bpas\b|\bpci\b/.test(haystack)) {
    return 'rci';
  }
  if (/\bpas\b|pavement assessment/.test(haystack)) {
    return 'pas';
  }
  if (/\bpci\b|pavement condition index/.test(haystack)) {
    return 'pci';
  }
  if (/\bcurvature\b|radius of curvature/.test(haystack) || (code === 'lcrv' && !/\bcrack/.test(haystack))) {
    return 'curvature';
  }
  if (/\bcrack|ltrc|lwcl|lwcr|lrcr\b/.test(haystack) || parameter === 'crackwidth') {
    return 'cracking';
  }
  if (/\brut|llrt|lrrt|llrd|lrrd|llbi|lrbi|ltad|ltrv\b/.test(haystack)) {
    return 'rutting';
  }
  if (/\blpv|lv3|lv10|ll03|ll10|lr03|lr10|evenness|ride\b/.test(haystack)) {
    return 'ride';
  }
  if (/\btexture|smtd|lltx|lltd|lltm|mpd|ltvv|lt05|lt95\b/.test(haystack)) {
    return 'texture';
  }
  if (/\bedge|ledr|les1|les2|ledc\b/.test(haystack)) {
    return 'edge';
  }
  if (surveyType === 'tempest' || surveyType === 'scanner') {
    return 'condition';
  }
  return 'condition';
}

export function getDefectBandMeaningLabels(
  family: DefectBandMeaningFamily,
): DefectBandMeaningLabels {
  return FAMILY_LABELS[family];
}

export function getDefectBandMeaningDescription(
  family: DefectBandMeaningFamily,
): string {
  return FAMILY_DESCRIPTIONS[family];
}

export function resolveDefectBandMeaningLabels(
  context: DefectBandMeaningContext,
): DefectBandMeaningLabels {
  return getDefectBandMeaningLabels(resolveDefectBandMeaningFamily(context));
}

export function resolveDefectBandMeaningDescription(
  context: DefectBandMeaningContext,
): string {
  return getDefectBandMeaningDescription(resolveDefectBandMeaningFamily(context));
}

export function defectBandMeaningLabelList(
  context: DefectBandMeaningContext,
): readonly string[] {
  const labels = resolveDefectBandMeaningLabels(context);
  return DEFECT_BAND_SLUGS.map((slug) => labels[slug]);
}
